import i18next from "i18next";
import { AsphaltCutter } from "../../interfaces/api/AsphaltCutter";
import { Bucket } from "../../interfaces/api/Bucket";
import { Compactor } from "../../interfaces/api/Compactor";
import { GradingBeam } from "../../interfaces/api/GradingBeam";
import { Grapple } from "../../interfaces/api/Grapple";
import { GrappleModule } from "../../interfaces/api/GrappleModule";
import { LowerBracket } from "../../interfaces/api/LowerBracket";
import { MachineFixing } from "../../interfaces/api/MachineFixing";
import { PropertyData } from "../../interfaces/api/PropertyData";
import { RailGrapple } from "../../interfaces/api/RailGrapple";
import { Ripper } from "../../interfaces/api/Ripper";
import { Rototilt } from "../../interfaces/api/Rototilt";
import { UpperBracket } from "../../interfaces/api/UpperBracket";
import { BucketType } from "../enums/BucketType";
import { GrappleType } from "../enums/GrappleType";
import { ProductType } from "../enums/ProductType";
import { ProductItem } from "../types/ProductItem";
import { QCIdentifier } from "../types/QCIdentifier";
import { localization } from "./productFilterHelper";

export type Product =
  | GrappleModule
  | LowerBracket
  | MachineFixing
  | Rototilt
  | UpperBracket
  | ProductItem[];

interface ProductDataProps {
  market: string;
  product: Product | ProductItem;
  productType?: ProductType;
  t: i18next.TFunction;
}

interface ProductProperty {
  label: string;
  text: string;
}

interface ColumnCartData {
  label: string;
  text: string;
}

interface RowCartData {
  leftColumn: ColumnCartData;
  rightColumn?: ColumnCartData;
}

interface ProductDataCartOutput {
  imageLink: string;
  name: string;
  nameLabel: string;
  rows: RowCartData[];
}

export interface ProductDataOutput {
  imageLink: string;
  name: string;
  nameLabel: string;
  properties: ProductProperty[];
}

interface ValueUnit {
  unit: string;
  value: number;
}

const getValueUnit = (
  propertyData: PropertyData,
  market: string
): ValueUnit => {
  const value = localization.value(propertyData, market);
  const unit = localization.unit(propertyData, market);

  return { unit, value };
};

const initProductData = (
  imageLink: string,
  name: string,
  nameLabel: string
): ProductDataOutput => {
  return {
    name,
    nameLabel,
    imageLink,
    properties: [],
  };
};

const isExcavationBucket = (type: string): boolean => {
  const excavationBuckeType = "Digger".toLowerCase();
  return type.toLowerCase() === excavationBuckeType;
};

const isQcFixing = (machineFixing: MachineFixing) => {
  return machineFixing.name.toLowerCase().indexOf(QCIdentifier) > -1;
};

const getBucketCategoryText = (type: string, t: i18next.TFunction) => {
  if (type.toLowerCase() === BucketType.Cable) {
    return t("trenchingbucket.trenchingbucket", {
      ns: "tools",
    });
  }

  if (type.toLowerCase() === BucketType.Digger) {
    return t("excavationbucket.excavationbucket", {
      ns: "tools",
    });
  }
  if (type.toLowerCase() === BucketType.Sorting) {
    return t("sortingbucket.sortingbucket", {
      ns: "tools",
    });
  }

  if (type.toLowerCase() === BucketType.Utility) {
    return t("gradingbucket.gradingbucket", {
      ns: "tools",
    });
  }

  return t("bucket.bucket", {
    ns: "tools",
  });
};

const getGrappleCategoryText = (type: string, t: i18next.TFunction) => {
  if (type.toLowerCase() === GrappleType.Multi) {
    return t("grappletypes.multigrapple", {
      ns: "tools",
    });
  }
  if (type.toLowerCase() === GrappleType.Stone) {
    return t("grappletypes.stonegrapple", {
      ns: "tools",
    });
  }
  if (type.toLowerCase() === GrappleType.Sorting) {
    return t("grappletypes.sortinggrapple", {
      ns: "tools",
    });
  }
  return t("grapple.grapple", {
    ns: "tools",
  });
};

const getToolsProductType = (key: string): ProductType => {
  const productType = key.split(".")[0];

  if (productType.toLowerCase() === "AsphaltCutter".toLowerCase()) {
    return ProductType.AsphaltCutters;
  }

  if (productType.toLowerCase() === "Compactor".toLowerCase()) {
    return ProductType.Compactors;
  }

  if (productType.toLowerCase() === "GradingBeam".toLowerCase()) {
    return ProductType.GradingBeams;
  }

  if (productType.toLowerCase() === "Grapple".toLowerCase()) {
    return ProductType.Grapples;
  }

  if (productType.toLowerCase() === "GrappleModule".toLowerCase()) {
    return ProductType.GrappleModules;
  }

  if (productType.toLowerCase() === "RailGrapple".toLowerCase()) {
    return ProductType.RailGrapples;
  }

  if (productType.toLowerCase() === "Ripper".toLowerCase()) {
    return ProductType.Rippers;
  }

  if (productType.toLowerCase() === "Bucket".toLowerCase()) {
    return ProductType.Buckets;
  }

  return undefined;
};

const getToolsProductTypeName = (
  productItem: ProductItem,
  productType: ProductType,
  t: i18next.TFunction
): string => {
  if (productType === ProductType.AsphaltCutters) {
    return t("asphaltcutter.asphaltcutter", {
      ns: "tools",
    });
  }

  if (productType === ProductType.Compactors) {
    return t("compactor.compactor", {
      ns: "tools",
    });
  }

  if (productType === ProductType.GradingBeams) {
    return t("gradingbeam.gradingbeam", {
      ns: "tools",
    });
  }

  if (productType === ProductType.Grapples) {
    const grapple = productItem as Grapple;
    return getGrappleCategoryText(grapple.family, t);
  }

  if (productType === ProductType.GrappleModules) {
    return t("grapplemodule.grapplemodule", {
      ns: "tools",
    });
  }

  if (productType === ProductType.RailGrapples) {
    return t("railroadgrapple.railroadgrapple", {
      ns: "tools",
    });
  }

  if (productType === ProductType.Rippers) {
    return t("ripper.ripper", {
      ns: "tools",
    });
  }

  if (productType === ProductType.Buckets) {
    const bucket = productItem as Bucket;
    return getBucketCategoryText(bucket.typeWithOutMarket, t);
  }

  return "?";
};

const getProductData = ({
  market,
  product,
  productType,
  t,
}: ProductDataProps): ProductDataOutput => {
  if (productType === ProductType.Rototilts) {
    const tiltrotator = product as Rototilt;
    const productData = initProductData(
      tiltrotator.imageLink,
      tiltrotator.name,
      t("tiltrotator", { ns: "tiltrotator" })
    );

    const { unit, value: maximumBreakingForce } = getValueUnit(
      tiltrotator.maximumBreakingForce,
      market
    );
    const { value: weightFrom, unit: maximumBucketWidth } = getValueUnit(
      tiltrotator.maximumBucketWidth,
      market
    );

    const labelMaximumBreakingForce = t("breakforcemax", { ns: "tiltrotator" });
    const textMaximumBreakingForce = `${maximumBreakingForce} ${unit}`;
    const labelMaximumBucketWidth = t("bucketwidthmax", { ns: "tiltrotator" });
    const textMaximumBucketWidth = `${weightFrom} ${maximumBucketWidth}`;
    productData.properties.push({
      label: labelMaximumBreakingForce,
      text: textMaximumBreakingForce,
    });

    productData.properties.push({
      label: labelMaximumBucketWidth,
      text: textMaximumBucketWidth,
    });

    return productData;
  }

  if (productType === ProductType.MachineFixings) {
    const machineFixing = product as MachineFixing;
    const isSFixing = !isQcFixing(machineFixing);
    const productData = initProductData(
      machineFixing.imageLink,
      machineFixing.name,
      isSFixing
        ? t("sfixing.sfixing", { ns: "machinefixings" })
        : t("qcfixing.qcfixing", { ns: "machinefixings" })
    );

    const { unit, value: maxLoad } = getValueUnit(
      machineFixing.maxLoad,
      market
    );

    const { value: weightFrom, unit: weightUnit } = getValueUnit(
      machineFixing.weight,
      market
    );
    const labelMaxLoad = isSFixing
      ? t("sfixing.maxallowedloadlifthook", {
          ns: "machinefixings",
        })
      : t("qcfixing.maxallowedloadlifthook", {
          ns: "machinefixings",
        });
    const textMaxLoad = `${maxLoad} ${unit}`;

    const labelWeight = isSFixing
      ? t("sfixing.weightfrom", { ns: "machinefixings" })
      : t("qcfixing.weightfrom", { ns: "machinefixings" });
    const textWeight = `${weightFrom} ${weightUnit}`;

    productData.properties.push({ label: labelMaxLoad, text: textMaxLoad });
    productData.properties.push({ label: labelWeight, text: textWeight });

    if (!isSFixing) {
      const { value: numberOfCouplers } = getValueUnit(
        machineFixing.maxNoOfCouplings,
        market
      );

      const labelNumberOfCouplers = t("qcfixing.numberofcouplers", {
        ns: "machinefixings",
      });
      const textNumberOfCouplers = `${numberOfCouplers}`;
      productData.properties.push({
        label: labelNumberOfCouplers,
        text: textNumberOfCouplers,
      });
    }

    return productData;
  }

  if (productType === ProductType.UpperBrackets) {
    const upperBracket = product as UpperBracket;
    const productData = initProductData(
      upperBracket.imageLink,
      upperBracket.name,
      t("attachment", {
        ns: "machinefixings",
      })
    );

    return productData;
  }

  if (productType === ProductType.LowerBrackets) {
    const lowerBracket = product as LowerBracket;
    const productData = initProductData(
      lowerBracket.imageLink,
      lowerBracket.name,
      t("toolattachment.toolattachment", {
        ns: "machinefixings",
      })
    );

    return productData;
  }

  if (productType === ProductType.GrappleModules) {
    const grappleModule = product as GrappleModule;
    const productData = initProductData(
      grappleModule.imageLink,
      grappleModule.name,
      t("grapplemodule.grapplemodule", {
        ns: "tools",
      })
    );

    const { unit: unitWeight, value: valueWeight } = getValueUnit(
      grappleModule.weight,
      market
    );
    const { unit: unitMaxLiftingCapacity, value: valueMaxLiftingCapacity } =
      getValueUnit(grappleModule.maxLiftingCapacity, market);

    const labelWeight = t("grapplemodule.weight", {
      ns: "tools",
    });
    const labelMaxLiftingCapacity = t("grapplemodule.liftingcapacitymax", {
      ns: "tools",
    });

    const textWeight = `${valueWeight} ${unitWeight}`;
    const textMaxLiftingCapacity = `${valueMaxLiftingCapacity} ${unitMaxLiftingCapacity}`;

    productData.properties.push({ label: labelWeight, text: textWeight });
    productData.properties.push({
      label: labelMaxLiftingCapacity,
      text: textMaxLiftingCapacity,
    });

    return productData;
  }

  if (!productType) {
    const productItem = product as ProductItem;
    const toolsProductType = getToolsProductType(productItem.partNumber.key);
    const nameLabel = getToolsProductTypeName(productItem, toolsProductType, t);
    const productData = initProductData(
      productItem.imageLink,
      productItem.name,
      nameLabel
    );

    if (toolsProductType === ProductType.AsphaltCutters) {
      const asphaltCutter = product as AsphaltCutter;
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        asphaltCutter.weight,
        market
      );
      const { unit: unitDepth, value: valueDepth } = getValueUnit(
        asphaltCutter.depth,
        market
      );

      const labelWeight = t("asphaltcutter.weight", {
        ns: "tools",
      });
      const labelDepth = t("asphaltcutter.cutdepth", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textDepth = `${valueDepth} ${unitDepth}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelDepth,
        text: textDepth,
      });

      return productData;
    }

    if (toolsProductType === ProductType.Buckets) {
      const bucket = product as Bucket;
      const { value: valuePartNumber } = getValueUnit(
        bucket.partNumber,
        market
      );
      const { unit: unitWidth, value: valueWidth } = getValueUnit(
        bucket.width,
        market
      );
      const { unit: unitVolume, value: valueVolume } = getValueUnit(
        bucket.volume,
        market
      );

      const labelArticleNumber = t("bucket.articlenumber", {
        ns: "tools",
      });
      const labelWidth = t("bucket.width", {
        ns: "tools",
      });
      const labelVolume = t("bucket.volume", {
        ns: "tools",
      });

      const textArticleNumber = `${valuePartNumber}`;
      const textWidth = `${valueWidth} ${unitWidth}`;
      const textVolume = `${valueVolume} ${unitVolume}`;

      productData.properties.push({
        label: labelArticleNumber,
        text: textArticleNumber,
      });
      productData.properties.push({
        label: labelWidth,
        text: textWidth,
      });

      if (isExcavationBucket(bucket.typeWithOutMarket) && bucket.toothType) {
        const textToothType = bucket.toothType;
        const labelToothType = t("excavationbucket.toothtype", {
          ns: "tools",
        });

        productData.properties.push({ label: labelVolume, text: textVolume });
        productData.properties.push({
          label: labelToothType,
          text: textToothType,
        });
      } else {
        productData.properties.push({ label: labelVolume, text: textVolume });
      }

      return productData;
    }

    if (toolsProductType === ProductType.Compactors) {
      const compactor = product as Compactor;
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        compactor.weight,
        market
      );
      const { unit: unitVibrationForce, value: valueVibrationForce } =
        getValueUnit(compactor.vibrationForce, market);

      const labelWeight = t("compactor.weight", {
        ns: "tools",
      });
      const labelVibrationForce = t("compactor.vibrationforce", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textVibrationForce = `${valueVibrationForce} ${unitVibrationForce}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelVibrationForce,
        text: textVibrationForce,
      });

      return productData;
    }

    if (toolsProductType === ProductType.GradingBeams) {
      const gradingBeam = product as GradingBeam;
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        gradingBeam.weight,
        market
      );
      const { unit: unitWidth, value: valueWidth } = getValueUnit(
        gradingBeam.width,
        market
      );

      const labelWeight = t("gradingbeam.weight", {
        ns: "tools",
      });
      const labelWidth = t("gradingbeam.width", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textWidth = `${valueWidth} ${unitWidth}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelWidth,
        text: textWidth,
      });

      return productData;
    }

    if (toolsProductType === ProductType.Grapples) {
      const grapple = product as Grapple;
      const {
        unit: unitGrippingForceTipToTip,
        value: valueGrippingForceTipToTip,
      } = getValueUnit(grapple.grippingForceTipToTip, market);
      const { unit: unitMaxGripWidth, value: valueMaxGripWidth } = getValueUnit(
        grapple.maxGripWidth,
        market
      );

      const labelGrippingForceTipToTip = t("grapple.grappleforcemax", {
        ns: "tools",
      });
      const labelMaxGripWidth = t("grapple.grapplewidthmax", {
        ns: "tools",
      });

      const textGrippingForceTipToTip = `${valueGrippingForceTipToTip} ${unitGrippingForceTipToTip}`;
      const textMaxGripWidth = `${valueMaxGripWidth} ${unitMaxGripWidth}`;

      productData.properties.push({
        label: labelGrippingForceTipToTip,
        text: textGrippingForceTipToTip,
      });
      productData.properties.push({
        label: labelMaxGripWidth,
        text: textMaxGripWidth,
      });

      return productData;
    }

    if (toolsProductType === ProductType.GrappleModules) {
      const grappleModule = product as GrappleModule;
      const { unit: unitMaxLiftingCapacity, value: valueMaxLiftingCapacity } =
        getValueUnit(grappleModule.maxLiftingCapacity, market);
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        grappleModule.weight,
        market
      );

      const labelWeight = t("grapplemodule.weight", {
        ns: "tools",
      });
      const labelMaxLiftingCapacity = t("grapplemodule.liftingcapacitymax", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textMaxLiftingCapacity = `${valueMaxLiftingCapacity} ${unitMaxLiftingCapacity}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelMaxLiftingCapacity,
        text: textMaxLiftingCapacity,
      });

      return productData;
    }

    if (toolsProductType === ProductType.RailGrapples) {
      const railGrapple = product as RailGrapple;
      const { unit: unitMaxLiftingCapacity, value: valueMaxLiftingCapacity } =
        getValueUnit(railGrapple.maxLiftingCapacity, market);
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        railGrapple.weight,
        market
      );

      const labelWeight = t("railroadgrapple.weight", {
        ns: "tools",
      });
      const labelMaxLiftingCapacity = t("railroadgrapple.liftingcapacitymax", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textMaxLiftingCapacity = `${valueMaxLiftingCapacity} ${unitMaxLiftingCapacity}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelMaxLiftingCapacity,
        text: textMaxLiftingCapacity,
      });

      return productData;
    }

    if (toolsProductType === ProductType.Rippers) {
      const ripper = product as Ripper;
      const { unit: unitWeight, value: valueWeight } = getValueUnit(
        ripper.weight,
        market
      );
      const { unit: unitHeight, value: valueHeight } = getValueUnit(
        ripper.height,
        market
      );

      const labelWeight = t("ripper.weight", {
        ns: "tools",
      });
      const labelHeight = t("ripper.toothradius", {
        ns: "tools",
      });

      const textWeight = `${valueWeight} ${unitWeight}`;
      const textHeight = `${valueHeight} ${unitHeight}`;

      productData.properties.push({ label: labelWeight, text: textWeight });
      productData.properties.push({
        label: labelHeight,
        text: textHeight,
      });

      return productData;
    }
  }

  return undefined;
};

const getCartProductData = ({
  market,
  product,
  productType,
  t,
}: ProductDataProps): ProductDataCartOutput => {
  const productData = getProductData({
    market,
    product,
    productType,
    t,
  });

  const isEven = (index: number): boolean => index % 2 === 0;

  const productDataCart: ProductDataCartOutput = {
    imageLink: productData.imageLink,
    name: productData.name,
    nameLabel: productData.nameLabel,
    rows: [],
  };

  productData.properties.forEach((property, index) => {
    if (isEven(index)) {
      const leftColumnCartData = {
        label: property.label,
        text: property.text,
      };

      if (index === productData.properties.length) {
        productDataCart.rows.push({ leftColumn: leftColumnCartData });
      }
    } else {
      const previousProductProperty = productData.properties[index - 1];
      const leftColumnCartData = {
        label: previousProductProperty.label,
        text: previousProductProperty.text,
      };
      const rightColumnCartData: ColumnCartData = {
        label: property.label,
        text: property.text,
      };

      productDataCart.rows.push({
        leftColumn: leftColumnCartData,
        rightColumn: rightColumnCartData,
      });
    }
  });

  return productDataCart;
};

const getHtmlContent = (productData: ProductDataOutput): string => {
  if (productData.properties.length === 0) {
    return "";
  }

  let content = "";

  productData.properties.forEach((property) => {
    if (content.length > 0) {
      content = content + "<br >";
    }

    content = content + `${property.label} | ${property.text}`;
  });

  return content;
};

export const productHelper = {
  getProductData,
  getCartProductData,
  getHtmlContent,
  getToolsProductType,
};
